import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import config from '../config';
import '../styles/CommunitySearch.css';
import DataSourcesModal from '../components/DataSourcesModal';

const API = config.API_URL || '/api';

// Groups that should never appear in search or featured lists
const EXCLUDED_GROUP_IDS = new Set([
  'b784ca63-a788-4551-bbb7-cd8cb4469d29',
  'cde80c0e-75fd-4785-a0be-879afb0f5bc0',
  '0c0ebf95-6029-4951-8d8d-becf3f26e0ab',
]);

// Unified discovery chips — real states + top cities from our DB.
// FL and CA have the most communities; cities ranked by group density.
const QUICK_CHIPS = [
  { label: 'Florida',         type: 'state', abbr: 'FL' },
  { label: 'California',      type: 'state', abbr: 'CA' },
  { label: 'New York',        type: 'state', abbr: 'NY' },
  { label: 'Miami',           type: 'city' },
  { label: 'Los Angeles',     type: 'city' },
  { label: 'Brooklyn',        type: 'city' },
  { label: 'Fort Lauderdale', type: 'city' },
  { label: 'Boca Raton',      type: 'city' },
  { label: 'Chicago',         type: 'city' },
  { label: 'Memphis',         type: 'city' },
  { label: 'Austin',          type: 'city' },
];

const GRADIENTS = [
  'linear-gradient(135deg,#1e1b4b,#4c1d95)',
  'linear-gradient(135deg,#0c1445,#1e3a8a)',
  'linear-gradient(135deg,#064e3b,#065f46)',
  'linear-gradient(135deg,#1c1917,#44403c)',
  'linear-gradient(135deg,#4a044e,#701a75)',
  'linear-gradient(135deg,#14532d,#166534)',
  'linear-gradient(135deg,#450a0a,#991b1b)',
  'linear-gradient(135deg,#172554,#1e40af)',
];

const TYPE_LABELS = {
  chabad: 'Chabad', orthodox: 'Orthodox', conservative: 'Conservative',
  reform: 'Reform', modern_orthodox: 'Modern Orthodox', sephardic: 'Sephardic',
  young_professionals: 'Young Prof.', learning: 'Learning Center',
  synagogue: 'Synagogue', shul: 'Shul', yeshiva: 'Yeshiva',
};

const gradientFor = (id = '') => {
  const n = String(id).split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return GRADIENTS[n % GRADIENTS.length];
};
const initials = (name = '') =>
  name.split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();

const formatEventDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  const now = new Date();
  const diff = Math.floor((d - now) / 86400000);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (diff === 0) return `Today · ${time}`;
  if (diff === 1) return `Tomorrow · ${time}`;
  if (diff > 1 && diff <= 6)
    return `${d.toLocaleDateString('en-US', { weekday: 'short' })} · ${time}`;
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${time}`;
};

// Converts any time string to minutes-since-midnight for reliable numeric sort.
// Handles: "HH:MM:SS", "HH:MM" (24h) and "H:MM AM/PM", "H AM/PM" (12h).
const timeToMinutes = (t = '') => {
  if (!t) return 9999;
  const ampm = /^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*(AM|PM)$/i.exec(t.trim());
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2] || '0', 10);
    const mer = ampm[3].toUpperCase();
    if (mer === 'PM' && h !== 12) h += 12;
    if (mer === 'AM' && h === 12) h = 0;
    return h * 60 + m;
  }
  const parts = t.split(':').map(Number);
  const h = parts[0]; const m = parts[1] || 0;
  return isNaN(h) ? 9999 : h * 60 + m;
};

const formatTime12 = (t = '') => {
  if (!t) return '';
  // 12-hour format: "7:30 PM", "12:00 AM", "7 PM", etc.
  const ampm = /^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*(AM|PM)$/i.exec(t.trim());
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2] || '0', 10);
    const mer = ampm[3].toUpperCase();
    if (mer === 'PM' && h !== 12) h += 12;
    if (mer === 'AM' && h === 12) h = 0;
    const display = (h % 12) || 12;
    const suffix  = h >= 12 ? 'pm' : 'am';
    return m === 0 ? `${display}${suffix}` : `${display}:${String(m).padStart(2,'0')}${suffix}`;
  }
  // 24-hour format: "19:30:00", "07:30", etc.
  const parts = t.split(':').map(Number);
  const h = parts[0]; const m = parts[1] || 0;
  if (isNaN(h)) return t;
  const display = (h % 12) || 12;
  const suffix  = h >= 12 ? 'pm' : 'am';
  return m === 0 ? `${display}${suffix}` : `${display}:${String(m).padStart(2,'0')}${suffix}`;
};

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const getPrayerEmoji = (title = '') => {
  const t = title.toLowerCase();
  if (t.includes('shachar'))                                  return '🌅';
  if (t.includes('mincha'))                                   return '🌇';
  if (t.includes('maariv') || t.includes("ma'ariv"))         return '🌙';
  if (t.includes('kabbalat') || t.includes('kabbalah'))      return '🕯️';
  if (t.includes('havdalah'))                                 return '✨';
  return '🕍';
};

const formatClassSchedule = (cl) => {
  if (cl.date) {
    const d = new Date(cl.date);
    if (!isNaN(d)) {
      const day  = DAY_SHORT[d.getDay()];
      const mon  = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
      const time = cl.start_time ? ` · ${formatTime12(cl.start_time)}` : '';
      return `${day}, ${mon} ${d.getDate()}${time}`;
    }
  }
  const rp = (cl.recurrence_pattern || cl.recurrence_day || '').toLowerCase();
  const dayMap = {
    sun:'Sun', mon:'Mon', tue:'Tue', wed:'Wed', thu:'Thu', fri:'Fri', sat:'Sat',
    sunday:'Sun', monday:'Mon', tuesday:'Tue', wednesday:'Wed',
    thursday:'Thu', friday:'Fri', saturday:'Sat',
  };
  let day = '';
  if (rp.includes('daily') || rp.includes('every day')) day = 'Daily';
  else if (rp.includes('weekday'))                       day = 'Mon–Fri';
  else if (rp.includes('weekly'))                        day = 'Weekly';
  else {
    const found = [...new Set(Object.keys(dayMap).filter(k => rp.includes(k)).map(k => dayMap[k]))];
    day = found.join('/');
  }
  const time = cl.start_time ? formatTime12(cl.start_time) : '';
  return [day, time].filter(Boolean).join(' · ') || null;
};

const prayerTotal = (p) => {
  if (!p) return 0;
  if (Array.isArray(p)) return p.length;
  return Object.values(p).reduce((s, a) => s + (a?.length || 0), 0);
};

const openAppStore = () => {
  const url = /android/i.test(navigator.userAgent || '')
    ? 'https://play.google.com/store/apps/details?id=us.yesod.app'
    : 'https://apps.apple.com/us/app/yesod/id6748472269';
  window.open(url, '_blank');
};

// ─── Avatar ───────────────────────────────────────────────────────────────────
const CommunityAvatar = ({ g, size = 52 }) => {
  const r = Math.round(size * 0.22);
  return g.logo_url ? (
    <img src={g.logo_url} alt={g.name} className="cs-avatar cs-avatar--img"
      style={{ width: size, height: size, borderRadius: r }} />
  ) : (
    <div className="cs-avatar cs-avatar--init"
      style={{ width: size, height: size, borderRadius: r, background: gradientFor(g.id), fontSize: size * 0.28 }}>
      {initials(g.name)}
    </div>
  );
};

// ─── EventCardMini — compact event/class card for location browse ────────────
const fmtEventDate = (item) => {
  const rawDate = item.computed_date || item.date;
  if (rawDate) {
    const d = new Date(String(rawDate).slice(0, 10) + 'T00:00:00');
    if (!isNaN(d)) return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }
  if (item.is_recurring) {
    const p = (item.recurrence_pattern || item.recurrence_day || '').toLowerCase();
    for (const day of ['sunday','monday','tuesday','wednesday','thursday','friday','shabbat']) {
      if (p.includes(day)) return day.charAt(0).toUpperCase() + day.slice(1) + 's';
    }
    return 'Recurring';
  }
  return '';
};

const EventCardMini = ({ item, onClick }) => {
  const isEvent  = item._type === 'event';
  const group    = item.group || {};
  const dateStr  = fmtEventDate(item);
  const typeLabel = isEvent ? 'Event' : item.is_recurring ? 'Weekly' : 'Class';
  return (
    <li className="cs-ecard" onClick={onClick} role="button" tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}>
      {item.cover_image_url
        ? <img src={item.cover_image_url} alt="" className="cs-ecard__bg cs-ecard__bg--img" loading="lazy" />
        : <div className="cs-ecard__bg" style={{ background: gradientFor(item.id) }} />}
      {group.logo_url && (
        <img src={group.logo_url} alt="" className="cs-ecard__logo" aria-hidden="true" loading="lazy" />
      )}
      <div className="cs-ecard__scrim" />
      <span className="cs-ecard__chip">{typeLabel}</span>
      <div className="cs-ecard__foot">
        {group.name && <p className="cs-ecard__org">{group.name}</p>}
        <p className="cs-ecard__title">{item.title}</p>
        {dateStr && <p className="cs-ecard__when">{dateStr}</p>}
      </div>
    </li>
  );
};

// ─── Community Card ───────────────────────────────────────────────────────────
const CommunityCard = ({ g, onClick }) => {
  const typeLabel = TYPE_LABELS[(g.community_type || '').toLowerCase()] || null;
  const location = [g.city, g.state].filter(Boolean).join(', ');
  return (
    <button className="cs-card" onClick={onClick}>
      <CommunityAvatar g={g} size={52} />
      <div className="cs-card__body">
        <h3 className="cs-card__name">{g.name}</h3>
        <div className="cs-card__row">
          {location && (
            <span className="cs-card__loc">
              <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor"
                strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              {location}
            </span>
          )}
          {typeLabel && <span className="cs-card__type">{typeLabel}</span>}
        </div>
      </div>
      <svg className="cs-card__arrow" viewBox="0 0 16 16" width="14" height="14"
        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M5 8h7M9 5l3 3-3 3"/>
      </svg>
    </button>
  );
};

// ─── Panel item sub-components ────────────────────────────────────────────────
const EventItem = ({ ev }) => (
  <div className="cp-item">
    <div className="cp-item__icon cp-item__icon--event">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <path d="M16 2v4M8 2v4M3 10h18"/>
      </svg>
    </div>
    <div className="cp-item__body">
      <span className="cp-item__title">{ev.title}</span>
      {ev.date && (
        <span className="cp-item__meta">{formatEventDate(ev.date)}</span>
      )}
    </div>
  </div>
);

const ClassItem = ({ cl }) => {
  const schedule = formatClassSchedule(cl);
  const meta     = [schedule, cl.teacher ? `with ${cl.teacher}` : ''].filter(Boolean).join(' · ');
  return (
    <div className="cp-item">
      <div className="cp-item__icon cp-item__icon--class">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
        </svg>
      </div>
      <div className="cp-item__body">
        <span className="cp-item__title">{cl.title}</span>
        {meta && <span className="cp-item__meta">{meta}</span>}
      </div>
    </div>
  );
};

const PRAYER_DAY_ORDER = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Shabbat'];

const PrayerTab = ({ prayer }) => {
  if (!prayer) return null;

  // Handle legacy flat array (fallback)
  if (Array.isArray(prayer)) {
    return [...prayer]
      .sort((a, b) => timeToMinutes(a.start_time || a.time) - timeToMinutes(b.start_time || b.time))
      .map((item, i) => (
      <div className="cp-prayer-row" key={i}>
        <span className="cp-prayer-row__emoji">{getPrayerEmoji(item.title)}</span>
        <span className="cp-prayer-row__title">{item.title}</span>
        {(item.start_time || item.time) && (
          <span className="cp-prayer-row__time">{formatTime12(item.start_time || item.time)}</span>
        )}
      </div>
    ));
  }

  // Grouped object { Sunday: [...], Monday: [...], ... }
  const todayIdx     = new Date().getDay();
  const todayFullDay = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][todayIdx];
  const todayKey     = todayFullDay === 'Saturday' ? 'Shabbat' : todayFullDay;

  const populated = PRAYER_DAY_ORDER.filter(d => prayer[d]?.length);
  const ordered   = [
    ...populated.filter(d => d === todayKey),
    ...populated.filter(d => d !== todayKey),
  ];

  if (!ordered.length) return null;

  return ordered.map(dayName => (
    <div className="cp-prayer-section" key={dayName}>
      <p className="cp-prayer-hd">
        {dayName === todayKey ? `Today · ${dayName}` : dayName}
      </p>
      {[...prayer[dayName]]
        .sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time))
        .map((item, i) => (
          <div className="cp-prayer-row" key={i}>
            <span className="cp-prayer-row__emoji">{getPrayerEmoji(item.title)}</span>
            <span className="cp-prayer-row__title">{item.title}</span>
            {item.time && (
              <span className="cp-prayer-row__time">{formatTime12(item.time)}</span>
            )}
          </div>
        ))}
    </div>
  ));
};

const SkelRow = () => (
  <div className="cp__skel-row">
    <div className="cs-skeleton" style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0 }} />
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div className="cs-skeleton" style={{ height: 12, width: '60%', borderRadius: 4 }} />
      <div className="cs-skeleton" style={{ height: 10, width: '38%', borderRadius: 4 }} />
    </div>
  </div>
);

// ─── Community Panel ──────────────────────────────────────────────────────────
const CommunityPanel = ({ g, onClose }) => {
  const [activeTab,     setActiveTab]     = useState(0);
  const [userPickedTab, setUserPickedTab] = useState(false);
  const [events,        setEvents]        = useState(null);
  const [classes,       setClasses]       = useState(null);
  const [prayer,        setPrayer]        = useState(null);
  const [dataReady,     setDataReady]     = useState(false);
  const [copied,        setCopied]        = useState(false);
  const [snack,          setSnack]         = useState(false);
  const [showDsModal,   setShowDsModal]   = useState(false);

  const typeLabel = TYPE_LABELS[(g.community_type || '').toLowerCase()] || null;
  const location  = [g.city, g.state].filter(Boolean).join(', ');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [evRes, clRes, prRes] = await Promise.allSettled([
          fetch(`${API}/public/groups/${g.id}/events?upcoming=true`),
          fetch(`${API}/public/groups/${g.id}/classes`),
          fetch(`${API}/public/groups/${g.id}/agenda`),
        ]);
        if (!alive) return;

        if (evRes.status === 'fulfilled' && evRes.value.ok) {
          const d = await evRes.value.json();
          // API returns upcoming events already sorted asc when ?upcoming=true
          setEvents((d.data || []).slice(0, 6));
        } else setEvents([]);

        if (clRes.status === 'fulfilled' && clRes.value.ok) {
          const d = await clRes.value.json();
          setClasses((d.data || []).slice(0, 6));
        } else setClasses([]);

        if (prRes.status === 'fulfilled' && prRes.value.ok) {
          const d = await prRes.value.json();
          const raw = d.data;
          // Store raw: either grouped object {Sunday:[...]} or flat array
          if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
            setPrayer(raw);
          } else {
            setPrayer(Array.isArray(raw) ? raw : []);
          }
        } else setPrayer({});
      } catch {
        setEvents([]); setClasses([]); setPrayer([]);
      }
      if (alive) setDataReady(true);
    })();
    return () => { alive = false; };
  }, [g.id]);

  // Auto-switch to first tab that has data (unless user already picked one)
  useEffect(() => {
    if (!dataReady || userPickedTab) return;
    const counts = [events?.length || 0, classes?.length || 0, prayerTotal(prayer)];
    const first = counts.findIndex(c => c > 0);
    if (first !== -1) setActiveTab(first);
  }, [dataReady, events, classes, prayer, userPickedTab]);

  const TABS = [
    { label: 'Events',   data: events,  count: events?.length  || 0, empty: 'No upcoming events listed.' },
    { label: 'Classes',  data: classes, count: classes?.length || 0, empty: 'No classes listed yet.' },
    { label: 'Minyanim', data: prayer,  count: prayerTotal(prayer), empty: 'No minyanim schedule listed.' },
  ];

  const renderItem = (item, i) => {
    if (activeTab === 0) return <EventItem key={i} ev={item} />;
    return                      <ClassItem key={i} cl={item} />;
  };

  const activeList    = activeTab === 2 ? null : TABS[activeTab].data;
  const hasActiveData = activeTab === 2 ? prayerTotal(prayer) > 0 : (activeList?.length || 0) > 0;

  const TAB_SLUGS = ['events', 'classes', 'minyanim'];
  const tabSlug   = TAB_SLUGS[activeTab] || 'events';
  const shareUrl  = `https://yesod.us/community/${encodeURIComponent(g.slug || g.id)}`;

  const showSnack = () => { setSnack(true); setTimeout(() => setSnack(false), 2500); };

  const handleShare = async () => {
    const shareData = { title: g.name, text: `Check out ${g.name} on Yesod`, url: shareUrl };
    // 1. Native share sheet (mobile) — no snackbar needed, OS handles confirmation
    if (navigator.share) {
      try { await navigator.share(shareData); return; } catch { /* cancelled — fall through */ }
    }
    // 2. Clipboard API (HTTPS / localhost)
    try {
      await navigator.clipboard.writeText(shareUrl);
      showSnack(); return;
    } catch { /* not secure context — fall through */ }
    // 3. execCommand fallback (HTTP / older browsers)
    try {
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
      document.body.appendChild(ta);
      ta.focus(); ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      showSnack();
    } catch { /* nothing worked */ }
  };

  return (
    <div className="cp-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cp" role="dialog" aria-modal="true" aria-label={g.name}>

        <div className="cp__handle" />

        {/* Compact header: avatar | name+loc | share + close */}
        <div className="cp__head">
          <CommunityAvatar g={g} size={56} />
          <div className="cp__head-meta">
            <h2 className="cp__name">{g.name}</h2>
            <div className="cp__head-row">
              {location && (
                <span className="cp__loc">
                  <svg viewBox="0 0 24 24" width="10" height="10" fill="none"
                    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  {location}
                </span>
              )}
              {typeLabel && <span className="cp__badge">{typeLabel}</span>}
            </div>
          </div>
          <div className="cp__head-actions">
            <button className={`cp__share${snack ? ' cp__share--done' : ''}`}
              onClick={handleShare} aria-label="Share community">
              {snack ? (
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                  <polyline points="16 6 12 2 8 6"/>
                  <line x1="12" y1="2" x2="12" y2="15"/>
                </svg>
              )}
            </button>
            <button className="cp__close" onClick={onClose} aria-label="Close">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Pinned tabs — always visible, not inside scroll */}
        <div className="cp__tabs" role="tablist">
          {TABS.map((t, i) => (
            <button key={t.label} role="tab" aria-selected={activeTab === i}
              className={`cp__tab${activeTab === i ? ' cp__tab--active' : ''}`}
              onClick={() => { setUserPickedTab(true); setActiveTab(i); }}>
              {t.label}
              {dataReady && t.count > 0 && (
                <span className="cp__tab-count">{t.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Scrollable area: description + tab content */}
        <div className="cp__scroll">
          {g.description && <p className="cp__desc">{g.description}</p>}

          <div className="cp__content" role="tabpanel">
            {!dataReady ? (
              <div className="cp__loading"><SkelRow /><SkelRow /><SkelRow /></div>
            ) : hasActiveData ? (
              <>
                {activeTab === 2 ? (
                  <PrayerTab prayer={prayer} />
                ) : (
                  <div className="cp__list">{activeList.map(renderItem)}</div>
                )}
                <button className="cp__see-more" onClick={openAppStore}>
                  See full schedule in the app →
                </button>
              </>
            ) : (
              <div className="cp__empty">
                <div className="cp__empty-icon">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
                    stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
                    <circle cx="12" cy="12" r="9"/>
                    <path d="M12 7v5l3 3"/>
                  </svg>
                </div>
                <p className="cp__empty-label">{TABS[activeTab].empty}</p>
                <p className="cp__empty-hint">Open Yesod to see the live schedule.</p>
              </div>
            )}
          </div>

          <div className="cp__ds-link">
            <button className="dsm-trigger" onClick={() => setShowDsModal(true)}>
              <svg viewBox="0 0 24 24" width="11" height="11" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="8"/>
                <line x1="12" y1="12" x2="12" y2="16"/>
              </svg>
              Publicly sourced community info
            </button>
          </div>
        </div>

        {/* Sticky CTA */}
        <div className="cp__cta">
          <button className="cp__cta-btn" onClick={openAppStore}>
            Get Yesod — Free
          </button>
        </div>

        <DataSourcesModal isOpen={showDsModal} onClose={() => setShowDsModal(false)} />

        {snack && (
          <div className="cp__snackbar" role="status">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Link copied to clipboard
          </div>
        )}

      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const CommunitySearchPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [query,       setQuery]       = useState(searchParams.get('q') || '');
  const [results,     setResults]     = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [featured,    setFeatured]    = useState([]);
  const [selected,    setSelected]    = useState(null);

  // ── State / city browse ───────────────────────────────────────────────────
  const [browseState,   setBrowseState]   = useState(null); // { name, abbr } or null
  const [browseGroups,  setBrowseGroups]  = useState([]); // all groups in selected state
  const [browseCities,  setBrowseCities]  = useState([]); // distinct cities in state
  const [browseCity,    setBrowseCity]    = useState(null); // selected city
  const [browseLoading,     setBrowseLoading]     = useState(false);
  const [browseEventsFeed,   setBrowseEventsFeed]   = useState([]);
  const [browseEventsLoading,setBrowseEventsLoading] = useState(false);

  const inputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    fetch(`${API}/public/groups/marquee-logos`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.data?.length) setFeatured(d.data.filter(g => !EXCLUDED_GROUP_IDS.has(g.id)).slice(0, 20)); })
      .catch(() => {});
  }, []);

  const doSearch = useCallback(async (q) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) { setResults([]); setHasSearched(false); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/public/groups/city-search?q=${encodeURIComponent(trimmed)}&limit=60`);
      if (r.ok) { const d = await r.json(); setResults((d.data || []).filter(g => !EXCLUDED_GROUP_IDS.has(g.id))); setHasSearched(true); }
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (query.trim().length >= 2) doSearch(query); }, []);

  const handleInput = (e) => {
    const q = e.target.value;
    setQuery(q);
    // Typing clears the state/city browse
    if (q.trim()) {
      setBrowseState(null); setBrowseGroups([]); setBrowseCities([]); setBrowseCity(null);
    }
    clearTimeout(timerRef.current);
    if (!q.trim()) { setResults([]); setHasSearched(false); return; }
    timerRef.current = setTimeout(() => doSearch(q), 260);
  };

  const handleQuickCity = (city) => {
    setQuery(city);
    // Clear browse when using quick city
    setBrowseState(null); setBrowseGroups([]); setBrowseCities([]); setBrowseCity(null);
    doSearch(city);
  };

  const clearSearch = () => {
    setQuery(''); setResults([]); setHasSearched(false);
    inputRef.current?.focus();
  };

  // ── State browse ─────────────────────────────────────────────────────────
  const handleBrowseState = useCallback(async (state) => {
    if (browseState?.abbr === state.abbr) {
      // toggle off
      setBrowseState(null); setBrowseGroups([]); setBrowseCities([]); setBrowseCity(null);
      setResults([]); setHasSearched(false);
      return;
    }
    setBrowseState(state); setBrowseCity(null);
    setResults([]); setHasSearched(false);
    setBrowseLoading(true);
    try {
      const r = await fetch(`${API}/public/groups/city-search?state=${encodeURIComponent(state.abbr)}&limit=500`);
      if (r.ok) {
        const d = await r.json();
        const groups = (d.data || []).filter(g => !EXCLUDED_GROUP_IDS.has(g.id));
        setBrowseGroups(groups);
        setResults(groups);
        setHasSearched(true);
        // Extract cities sorted by group count
        const cityCount = {};
        groups.forEach(g => { if (g.city) cityCount[g.city] = (cityCount[g.city] || 0) + 1; });
        const cities = Object.entries(cityCount)
          .sort(([, a], [, b]) => b - a)
          .map(([c]) => c);
        setBrowseCities(cities);
      }
    } catch { /* silent */ }
    setBrowseLoading(false);
  }, [browseState]);

  const handleBrowseCity = useCallback((city) => {
    if (browseCity === city) {
      // toggle off — show all state groups
      setBrowseCity(null);
      setResults(browseGroups);
      return;
    }
    setBrowseCity(city);
    setResults(browseGroups.filter(g => g.city === city));
    setHasSearched(true);
  }, [browseCity, browseGroups]);

  const clearBrowse = () => {
    setBrowseState(null); setBrowseGroups([]); setBrowseCities([]); setBrowseCity(null);
    setResults([]); setHasSearched(false);
    setBrowseEventsFeed([]);
  };

  // ── Reactive events/classes feed for browse by state or city ─────────────
  useEffect(() => {
    if (!browseState) { setBrowseEventsFeed([]); return; }
    let alive = true;
    (async () => {
      setBrowseEventsLoading(true);
      try {
        const params = new URLSearchParams({ state: browseState.abbr, limit: 16 });
        if (browseCity) params.set('city', browseCity);
        const r = await fetch(`${API}/public/groups/nearby-feed?${params}`);
        if (r.ok && alive) {
          const d = await r.json();
          setBrowseEventsFeed((d.data || []).filter(item => !EXCLUDED_GROUP_IDS.has(item.group?.id)));
        }
      } catch { /* silent */ }
      if (alive) setBrowseEventsLoading(false);
    })();
    return () => { alive = false; };
  }, [browseState, browseCity]);

  const showFeatured = !hasSearched && !loading && !browseLoading && featured.length > 0;
  const showEmpty    = hasSearched && !loading && !browseLoading && results.length === 0;
  const isBrowsing   = !!browseState && !query;

  return (
    <div className="cs">

      <header className="cs-header">
        <button className="cs-back" onClick={() => navigate(-1)} aria-label="Back">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M19 12H5M9 6l-6 6 6 6"/>
          </svg>
        </button>
        <span className="cs-header__brand">Yesod</span>
        <div style={{ width: 36 }} />
      </header>

      <div className="cs-hero">
        <h1 className="cs-hero__title">Find your<br />community</h1>
        <p className="cs-hero__sub">Events · Classes · Prayer times · All in one place</p>

        <div className="cs-search-wrap">
          <svg className="cs-search-wrap__icon" viewBox="0 0 20 20" width="16" height="16"
            fill="none" aria-hidden="true">
            <circle cx="9" cy="9" r="6" stroke="#9ca3af" strokeWidth="1.75"/>
            <path d="M13.5 13.5l3 3" stroke="#9ca3af" strokeWidth="1.75" strokeLinecap="round"/>
          </svg>
          <input ref={inputRef} className="cs-search-input" type="search" inputMode="search"
            placeholder="Search by name, city, state, or zip code…"
            value={query} onChange={handleInput}
            autoComplete="off" autoCorrect="off" spellCheck="false" />
          {query && (
            <button className="cs-search-clear" onClick={clearSearch} aria-label="Clear">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          )}
        </div>

        {/* Unified chips: states + real cities — only when no query and not browsing a state */}
        {!query && !browseState && (
          <div className="cs-chips">
            {QUICK_CHIPS.map(chip =>
              chip.type === 'state' ? (
                <button
                  key={chip.abbr}
                  className="cs-chip cs-chip--state"
                  onClick={() => handleBrowseState({ name: chip.label, abbr: chip.abbr })}
                >
                  {chip.label}
                </button>
              ) : (
                <button key={chip.label} className="cs-chip" onClick={() => handleQuickCity(chip.label)}>
                  {chip.label}
                </button>
              )
            )}
          </div>
        )}

        {/* Breadcrumb + city chips when a state is selected */}
        {!query && browseState && (
          <>
            <div className="cs-browse__header" style={{ marginTop: 14 }}>
              <button className="cs-browse__back" onClick={clearBrowse}>
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M19 12H5M9 6l-6 6 6 6"/>
                </svg>
                All
              </button>
              <span className="cs-browse__cur">
                {browseState.name}{browseCity && <> › {browseCity}</>}
              </span>
            </div>
            {browseCities.length > 0 && (
              <div className="cs-chips cs-chips--cities" style={{ marginTop: 10 }}>
                <button
                  className={`cs-chip cs-chip--all${!browseCity ? ' cs-chip--active' : ''}`}
                  onClick={() => handleBrowseCity(null)}
                >All {browseState.name}</button>
                {browseCities.map(c => (
                  <button
                    key={c}
                    className={`cs-chip${browseCity === c ? ' cs-chip--active' : ''}`}
                    onClick={() => handleBrowseCity(c)}
                  >{c}</button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div className="cs-body">

        {(loading || browseLoading) && (
          <div className="cs-list">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="cs-card cs-card--skeleton">
                <div className="cs-skeleton" style={{ width: 52, height: 52, borderRadius: 12, flexShrink: 0 }} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
                  <div className="cs-skeleton" style={{ height: 13, width: '55%', borderRadius: 4 }} />
                  <div className="cs-skeleton" style={{ height: 10, width: '32%', borderRadius: 4 }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !browseLoading && hasSearched && results.length > 0 && (
          <>
            <p className="cs-results-label">
              {isBrowsing
                ? browseCity
                  ? `${results.length} communities in ${browseCity}, ${browseState.abbr}`
                  : `${results.length} communities in ${browseState.name}`
                : `${results.length} communities found`}
            </p>

            {/* ── Events/classes strip — updates dynamically with state/city ── */}
            {isBrowsing && (browseEventsLoading || browseEventsFeed.length > 0) && (
              <div className="cs-browse-events">
                <p className="cs-browse-events__hd">
                  Upcoming {browseCity ? `in ${browseCity}` : `in ${browseState?.name}`}
                </p>
                <ul className="cs-ecards">
                  {browseEventsLoading
                    ? [...Array(4)].map((_, i) => (
                        <li key={i} className="cs-ecard cs-ecard--skel">
                          <div className="cs-ecard__bg" style={{ background: '#e4e4e7' }} />
                        </li>
                      ))
                    : browseEventsFeed.map(item => (
                        <EventCardMini
                          key={`${item._type}-${item.id}`}
                          item={item}
                          onClick={() => {
                            const slug = item.group?.slug || item.group?.id;
                            window.open(
                              item._type === 'event'
                                ? `/community/${slug}/event/${item.id}`
                                : `/community/${slug}/class/${item.id}`,
                              '_blank'
                            );
                          }}
                        />
                      ))}
                </ul>
              </div>
            )}

            <div className="cs-list">
              {results.map(g => <CommunityCard key={g.id} g={g} onClick={() => setSelected(g)} />)}
            </div>
          </>
        )}

        {showEmpty && (
          <div className="cs-empty">
            <div className="cs-empty__icon">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
                stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            </div>
            <h3 className="cs-empty__title">No communities found</h3>
            <p className="cs-empty__sub">Try a different city, state, or zip code</p>
            <button className="cs-empty__cta" onClick={isBrowsing ? clearBrowse : clearSearch}>
              {isBrowsing ? 'Back to States' : 'Clear search'}
            </button>
          </div>
        )}

        {showFeatured && (
          <>
            <p className="cs-results-label">Featured communities</p>
            <div className="cs-list">
              {featured.map(g => <CommunityCard key={g.id} g={g} onClick={() => setSelected(g)} />)}
            </div>
          </>
        )}

      </div>

      {selected && <CommunityPanel g={selected} onClose={() => setSelected(null)} />}

    </div>
  );
};

export default CommunitySearchPage;